import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { LayerDefinition } from "../../../models/mapConfiguration";
import type { LayerResults } from "../hooks/layerQueries";

import { QUERY_KEYS } from "../../../api/queries";
import { useVerbiageQuery } from "../../../api/verbiageApi";
import { useLayerQueries } from "../hooks/layerQueries";
import { hasPendingServerChanges, markExecuted } from "../hooks/queries/serverFilters";
import { createLayer, LayerKeyedArray, withSavedLayerIds } from "./layer";

interface MapLayersContextValue {
	layers: LayerKeyedArray;
	loadLayers: (layers: LayerDefinition[]) => void;
	syncLayerIds: (saved: LayerDefinition[]) => void;
	addLayer: (layer?: Partial<LayerDefinition>) => LayerDefinition;
	removeLayer: (id: string) => void;
	updateLayer: (id: string, update: (layer: LayerDefinition) => LayerDefinition) => void;
	reorderLayers: (fromIndex: number, toIndex: number) => void;
	executeAll: () => void;
	hasPendingChanges: boolean;
}

const MapLayersContext = createContext<MapLayersContextValue | null>(null);
const LayerResultsContext = createContext<LayerResults | null>(null);

export function useMapLayers() {
	const context = useContext(MapLayersContext);
	if (!context) throw new Error("useMapLayers must be used within a MapLayersProvider");
	return context;
}

export function useLayerResults() {
	const context = useContext(LayerResultsContext);
	if (!context) throw new Error("useLayerResults must be used within a MapLayersProvider");
	return context;
}

export function MapLayersProvider({ children }: { children: React.ReactNode }) {
	const [layers, setLayers] = useState(() => new LayerKeyedArray([]));
	const [executeRequest, setExecuteRequest] = useState(0);
	const results = useLayerQueries(layers.data);
	const queryClient = useQueryClient();
	const { verbiage } = useVerbiageQuery();

	useEffect(() => {
		if (!executeRequest) return;
		// Runs after the new query keys are observed, so a layer whose filters changed is already fetching
		// and is not restarted; layers with unchanged filters are refetched.
		void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.Maps.MapLayer] }, { cancelRefetch: false });
	}, [executeRequest, queryClient]);

	const loadLayers = useCallback((definitions: LayerDefinition[]) => {
		setLayers(new LayerKeyedArray(definitions.map(markExecuted)));
	}, []);

	const syncLayerIds = useCallback((saved: LayerDefinition[]) => {
		setLayers((prev) => new LayerKeyedArray(withSavedLayerIds(prev.data, saved)));
	}, []);

	const addLayer = useCallback(
		(layer: Partial<LayerDefinition> = {}) => {
			const created = createLayer(layer, layers.data.length, { verbiage });
			setLayers((prev) => {
				const ordinal = Math.max(-1, ...prev.data.map((l) => l.ordinal ?? -1)) + 1;
				return new LayerKeyedArray([...prev.data, { ...created, ordinal }]);
			});
			return created;
		},
		[layers.data.length, verbiage],
	);

	const removeLayer = useCallback((id: string) => {
		setLayers((prev) => new LayerKeyedArray(prev.data.filter((layer) => layer.id !== id)));
	}, []);

	const updateLayer = useCallback((id: string, update: (layer: LayerDefinition) => LayerDefinition) => {
		setLayers((prev) => {
			const current = prev.get(id);
			if (!current) return prev;
			const next = update(current);
			return next === current ? prev : new LayerKeyedArray(prev.data.map((l) => (l === current ? next : l)));
		});
	}, []);

	const reorderLayers = useCallback((fromIndex: number, toIndex: number) => {
		setLayers((prev) => {
			const reordered = [...prev.data];
			const [moved] = reordered.splice(fromIndex, 1);
			reordered.splice(toIndex, 0, moved);
			return new LayerKeyedArray(
				reordered.map((layer, ordinal) => (layer.ordinal === ordinal ? layer : { ...layer, ordinal })),
			);
		});
	}, []);

	const executeAll = useCallback(() => {
		setLayers((prev) => new LayerKeyedArray(prev.data.map(markExecuted)));
		setExecuteRequest((count) => count + 1);
	}, []);

	const hasPendingChanges = useMemo(() => layers.data.some(hasPendingServerChanges), [layers]);

	const value = useMemo(
		() => ({
			layers,
			loadLayers,
			syncLayerIds,
			addLayer,
			removeLayer,
			updateLayer,
			reorderLayers,
			executeAll,
			hasPendingChanges,
		}),
		[
			layers,
			loadLayers,
			syncLayerIds,
			addLayer,
			removeLayer,
			updateLayer,
			reorderLayers,
			executeAll,
			hasPendingChanges,
		],
	);

	return (
		<MapLayersContext.Provider value={value}>
			<LayerResultsContext.Provider value={results}>{children}</LayerResultsContext.Provider>
		</MapLayersContext.Provider>
	);
}
